import type { JsonValue } from '@bufbuild/protobuf';
import { Reading } from '../models/reading';

// The payload format is not settled in the platform yet. These are the keys the devices send today (see the sample
// CSVs in the design); every one of them may be missing or change type between readings.

export type JsonObject = { [key: string]: JsonValue };

export const asObject = (payload: JsonValue): JsonObject | undefined =>
  payload !== null && typeof payload === 'object' && !Array.isArray(payload) ? (payload as JsonObject) : undefined;

export const readNumber = (payload: JsonValue, key: string): number | undefined => {
  const value = asObject(payload)?.[key];
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  return undefined;
};

export const readString = (payload: JsonValue, key: string): string | undefined => {
  const value = asObject(payload)?.[key];
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
};

export const readBoolean = (payload: JsonValue, key: string): boolean | undefined => {
  const value = asObject(payload)?.[key];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') return true;
    if (normalized === 'false' || normalized === '0') return false;
  }
  return undefined;
};

export interface WeatherSample {
  time: Date;
  temperature?: number;
  humidity?: number;
  pressure?: number;
  windSpeed?: number;
  windDirection?: number;
  rainfallTotal?: number;
  rainfallCounter?: number;
  battery?: number;
}

export const toWeatherSample = (reading: Reading): WeatherSample => ({
  time: reading.time,
  temperature: readNumber(reading.payload, 'temperature'),
  humidity: readNumber(reading.payload, 'humidity'),
  pressure: readNumber(reading.payload, 'pressure'),
  windSpeed: readNumber(reading.payload, 'wind_speed'),
  windDirection: readNumber(reading.payload, 'wind_direction'),
  rainfallTotal: readNumber(reading.payload, 'rainfall_total'),
  rainfallCounter: readNumber(reading.payload, 'rainfall_counter'),
  battery: readNumber(reading.payload, 'battery'),
});

export interface GeoSample {
  time: Date;
  latitude: number;
  longitude: number;
  altitude?: number;
  speed?: number;
  angle?: number;
  satellites?: number;
  ignition?: number;
  battery?: number;
}

const isCoordinate = (lat?: number, lon?: number) =>
  lat !== undefined && lon !== undefined && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && !(lat === 0 && lon === 0);

/**
 * Readings without a usable position are dropped: the tracker flags them with `gps_valid: false`, and a missing fix can
 * also show up as no coordinates or 0,0. They are only hidden from the map and the table; the CSV keeps every reading.
 */
export const toGeoSample = (reading: Reading): GeoSample | undefined => {
  if (readBoolean(reading.payload, 'gps_valid') === false) return undefined;
  const latitude = readNumber(reading.payload, 'latitude');
  const longitude = readNumber(reading.payload, 'longitude');
  if (!isCoordinate(latitude, longitude)) return undefined;
  return {
    time: reading.time,
    latitude: latitude as number,
    longitude: longitude as number,
    altitude: readNumber(reading.payload, 'altitude_metre'),
    speed: readNumber(reading.payload, 'speed_kmh'),
    angle: readNumber(reading.payload, 'angle'),
    satellites: readNumber(reading.payload, 'satellites'),
    ignition: readNumber(reading.payload, 'ignition'),
    battery: readNumber(reading.payload, 'battery_percent'),
  };
};

/** Last value of a field across samples ordered oldest first. */
export function latestOf<T, K extends keyof T>(
  samples: T[],
  key: K,
): { value: NonNullable<T[K]>; index: number } | undefined {
  for (let i = samples.length - 1; i >= 0; i--) {
    const value = samples[i][key];
    if (value !== undefined && value !== null) return { value: value as NonNullable<T[K]>, index: i };
  }
  return undefined;
}
